import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AccountTagsCard } from "../../components/tags/AccountTagsCard";
import {
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
  SimpleGrid,
  Skeleton,
  Rating,
  Modal,
  Textarea,
} from "@mantine/core";
import { IconArrowLeft, IconBan, IconCheck } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { usersApi } from "../../api/users";
import type { UserStatus } from "../../types/domain";
import { PageHeader } from "../../components/common/PageHeader";
import { StatusBadge } from "../../components/common/StatusBadge";
import { QueryError } from "../../components/common/QueryError";
import { formatDate } from "../../lib/format";
import { errorMessage } from "../../lib/axios";
import { S3Avatar } from "../../components/common/S3Avatar";

function Field({ label, value }: { label: string; value?: string | number }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm">{value ?? "—"}</Text>
    </Stack>
  );
}

export function UserDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { data: user, isLoading, error, refetch } = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersApi.get(id),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (vars: { status: UserStatus; reason?: string }) =>
      usersApi.setStatus(id, vars.status, vars.reason),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Status updated" });
      setSuspendOpen(false);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["user", id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  if (isLoading) {
    return (
      <Stack>
        <Skeleton h={40} w={240} />
        <Skeleton h={200} />
      </Stack>
    );
  }
  if (error || !user) {
    return <QueryError error={error ?? new Error("Not found")} onRetry={refetch} />;
  }

  const p = user.personalDetails ?? {};
  const status = user.statusDetails?.currentStatus;
  const isActive = status === "active" || !status;

  return (
    <>
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate("/users")}
        mb="sm"
        pl={0}
      >
        Back to riders
      </Button>

      <PageHeader
        title={p.name ?? "Rider"}
        subtitle={p.phoneNumber}
        actions={
          isActive ? (
            <Button
              variant="light"
              color="red"
              leftSection={<IconBan size={16} />}
              onClick={() => setSuspendOpen(true)}
            >
              Suspend
            </Button>
          ) : (
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate({ status: "active" })}
            >
              Reactivate
            </Button>
          )
        }
      />

      <AccountTagsCard subject="rider" accountId={id} />

      <Card withBorder radius="md" padding="lg">
        <Group mb="md">
          <S3Avatar src={p.profilePicUrl} size={64} radius="md" alt={p.name}>
            {p.name?.charAt(0)?.toUpperCase() ?? "U"}
          </S3Avatar>
          <Stack gap={4}>
            <Group gap="xs">
              <Title order={4}>{p.name ?? "—"}</Title>
              <StatusBadge status={status} />
            </Group>
            {user.ratingDetails?.currentRating ? (
              <Rating
                value={user.ratingDetails.currentRating}
                fractions={2}
                readOnly
                size="sm"
              />
            ) : (
              <Text size="xs" c="dimmed">
                No ratings yet
              </Text>
            )}
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
          <Field label="Email" value={p.email} />
          <Field label="Phone" value={p.phoneNumber} />
          <Field label="Gender" value={p.gender} />
          <Field label="Referral code" value={user.referralDetails?.code} />
          <Field
            label="Referred"
            value={user.referralDetails?.usersReferred?.length ?? 0}
          />
          <Field label="Joined" value={formatDate(user.createdAt)} />
        </SimpleGrid>
      </Card>

      <Modal
        opened={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        title="Suspend rider"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            The rider will be blocked from booking. Provide a reason.
          </Text>
          <Textarea
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSuspendOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={statusMutation.isPending}
              disabled={reason.trim().length < 3}
              onClick={() =>
                statusMutation.mutate({ status: "suspended", reason: reason.trim() })
              }
            >
              Suspend
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
